import type { FieldComponent } from '@atlas/react-forms';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import pushToDataLayer, { btag } from 'utils/dataLayer';
import { useTranslation } from 'utils/translations';
import Errors from '../../Errors';
import Input from '../Input';

import styles from './String.module.scss';

const StringField: FieldComponent = ({ onChange, field, onFocus, onBlur }) => {
  const [showError, setShowError] = useState<boolean>(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  const initialValue = field.value?.toString() ?? '';
  const placeholder = useTranslation(field.label.toLowerCase()); // get a11y by field.label

  const handleChangeValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value, true);
    },
    [onChange],
  );

  const handleBlur = () => {
    onBlur?.();
    setShowError(true);
  };

  const handleFocus = () => {
    onFocus?.();
    setShowError(false);
  };

  const placeholderText = `${placeholder}${field.isRequired ? ' *' : ''}`;

  const focusInput = useCallback(() => {
    fieldRef?.current?.focus();
  }, []);

  useEffect(() => {
    if (field.name === 'EMAIL' && !field.errors.length) {
      pushToDataLayer('email_filled', btag);
    }
  }, [field.name, field.errors]);

  return (
    <div
      className={styles['form-field-container']}
      onClick={focusInput}
      onKeyDown={focusInput}
    >
      <Input
        ref={fieldRef}
        value={initialValue}
        onChange={handleChangeValue}
        type="text"
        autoFocus={field.autofocus}
        name={field.name}
        id={field.name}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={field.disabled || !field.isModifiable}
        placeholder={placeholderText}
        isValid={field?.errors?.length === 0}
      />
      {showError && <Errors errors={field.errors} />}
    </div>
  );
};

export default StringField;
